/**
 * Review Form Component
 * Form for creating or editing a tool review
 */

import { useState } from "react";
import { StarRating } from "./StarRating";
import { cn } from "@/utils/cn";
import type { ReviewCreateRequest, MyReview } from "@/types/tools";

interface ReviewFormProps {
  /** Existing review for editing (omit for create) */
  existingReview?: MyReview | null;
  /** Callback when form is submitted */
  onSubmit: (data: ReviewCreateRequest) => void;
  /** Callback when form is cancelled */
  onCancel?: () => void;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Additional className */
  className?: string;
}

export function ReviewForm({
  existingReview,
  onSubmit,
  onCancel,
  isSubmitting = false,
  className,
}: ReviewFormProps) {
  const [rating, setRating] = useState(existingReview?.rating ?? 0);
  const [reviewTitle, setReviewTitle] = useState(
    existingReview?.reviewTitle ?? "",
  );
  const [reviewText, setReviewText] = useState(
    existingReview?.reviewText ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!existingReview;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (rating === 0) {
      setError("Please select a rating");
      return;
    }

    onSubmit({
      rating,
      reviewTitle: reviewTitle.trim() || undefined,
      reviewText: reviewText.trim() || undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4",
        className,
      )}
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {isEditing ? "Edit Your Review" : "Write a Review"}
      </h3>

      {/* Rating */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Your Rating *
        </label>
        <StarRating
          value={rating}
          onChange={setRating}
          size="lg"
          disabled={isSubmitting}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>

      {/* Title */}
      <div className="mb-4">
        <label
          htmlFor="reviewTitle"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Review Title (optional)
        </label>
        <input
          id="reviewTitle"
          type="text"
          value={reviewTitle}
          onChange={(e) => setReviewTitle(e.target.value)}
          placeholder="Summarize your experience"
          maxLength={255}
          disabled={isSubmitting}
          className={cn(
            "w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600",
            "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100",
            "placeholder-gray-400 dark:placeholder-gray-500",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        />
      </div>

      {/* Review Text */}
      <div className="mb-4">
        <label
          htmlFor="reviewText"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Your Review (optional)
        </label>
        <textarea
          id="reviewText"
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="Share your experience with this tool..."
          rows={4}
          maxLength={2000}
          disabled={isSubmitting}
          className={cn(
            "w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600",
            "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100",
            "placeholder-gray-400 dark:placeholder-gray-500",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400",
            "disabled:opacity-50 disabled:cursor-not-allowed resize-none",
          )}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {reviewText.length}/2000 characters
        </p>
      </div>

      {/* Note about approval */}
      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Your review will be visible to others after it has been approved by an
          administrator.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium",
              "text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700",
              "hover:bg-gray-200 dark:hover:bg-gray-600",
              "focus:outline-none focus:ring-2 focus:ring-gray-400",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting || rating === 0}
          className={cn(
            "px-4 py-2 rounded-md text-sm font-medium",
            "text-white bg-blue-600 hover:bg-blue-700",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {isSubmitting
            ? "Submitting..."
            : isEditing
              ? "Update Review"
              : "Submit Review"}
        </button>
      </div>
    </form>
  );
}
